import { Formik, Form } from 'formik'
import { Helmet } from 'react-helmet-async'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { NavLink, useHistory, Redirect } from 'react-router-dom'
import styled from 'styled-components'
import * as Yup from 'yup'
import { Box, Button } from '@machobear/design-system'
import { TextButton } from '@/components/button'
import ContentWrapper from '@/components/content-wrapper'
import { Input, PhoneInput } from '@/components/form'
import Icon from '@/components/icon'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import Panel from '@/components/panel'
import { toast } from '@/components/toast-notifications'
import VerifiedBadge from '@/components/verified-badge'
import { show as showVerifyPhoneModal } from '@/components/verify-phone-modal'
import useProfile from '@/hooks/use-profile'
import { changePhoneNewNumber } from '@/models/requests/ports'
import { select } from '@/store'
import { colors, fontWeights, fontSizes, breakpoints } from '@/styles'
import { isNullOrEmpty } from '@/utils'
import { IdVerificationButton } from '@/features/Persona'

const StyledContentWrapper = styled(ContentWrapper)`
  max-width: 900px;
  margin-bottom: 50px;

  @media screen and (max-width: ${breakpoints.desktop}) {
    padding: 0 30px;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    padding: 0 10px;
  }
`

const PageHeader = styled.div`
  position: relative;
  margin-top: 50px;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin-top: 60px;
  }
`

const BackButton = styled(TextButton)`
  margin-top: 0;
  text-align: left;
  max-width: 200px;
  display: flex;
  position: absolute;
  left: 0px;
  top: 0px;
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  color: ${colors.secondary};

  > svg {
    margin-top: -2px;
    margin-right: 10px;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    top: -40px;
    left: 10px;
    font-size: 14px;

    > svg {
      height: 15px;
      width: 18px;
      margin-top: 3px;
      margin-right: 5px;
    }
  }
`

const StepsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`

const StepWrapper = styled.div`
  display: flex;
  align-items: center;

  svg {
    margin-right: 15px;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    flex-direction: column;

    svg {
      margin-right: 0;
      margin-bottom: 5px;
      height: 20px;
      width: 20px;
    }
  }

  ${props =>
    props.showDivider &&
    `
    padding-right: 140px;
    position: relative;

    :after {
      content: '';
      position: absolute;
      border-bottom: 1px dashed ${colors.mediumGrey};
      top: 10px;
      right: 20px;
      width: 100px;
    }

    @media screen and (max-width: ${breakpoints.tabletLandscape}) {
      padding-right: 90px;

      :after {
        width: 50px;
      }
    }
  `}

  ${props =>
    props.disabled &&
    `
    p {
      color: ${colors.mediumGrey};
    }

    svg {
      g {
        stroke: #c5c5c5
      }
    }
  `}
`

const StepText = styled.p`
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.bold};
  letter-spacing: -0.2px;
  line-height: 22px;
  margin: 0;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    font-size: 12px;
    line-height: 17px;
  }
`

const PanelContainer = styled(Panel)`
  margin: 50px auto 70px;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin: 30px auto;
  }
`

const PanelInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 500px;
  margin: 0 auto;

  :not(:last-child) {
    margin-bottom: 40px;
    padding-bottom: 40px;
    border-bottom: 1px solid ${colors.grey};
  }

  ${props =>
    props.disabled &&
    `
    opacity: 0.5;
    pointer-events: none;
  `}
`

const Title = styled.h2`
  font-size: 20px;
  font-weight: ${fontWeights.bold};
  line-height: 27px;
  text-align: center;
  margin: 0;
`

const Description = styled.p`
  font-size: 14px;
  color: ${colors.mediumGrey};
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.2px;
  line-height: 19px;
  text-align: center;
  margin: 32px 0 40px;
`

const DescriptionSmall = styled(Description)`
  font-size: 12px;
  line-height: 17px;
  margin: 0;
  max-width: 440px;
`
const CheckBoxWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  label {
    font-weight: ${fontWeights.medium};
    letter-spacing: -0.2px;
    line-height: 22px;
    margin-left: 15px;
  }
`

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 320px;
`

const CheckBox = styled(Input)``

const Bold = styled.span`
  font-weight: ${fontWeights.medium};
  margin-left: 3px;
`

const Step = ({ complete, text, showDivider, disabled }) => {
  return (
    <StepWrapper showDivider={showDivider} disabled={disabled}>
      <Icon id={complete ? 'checked_green' : 'checked_green_empty'} width={25} height={25} />
      <StepText>{text}</StepText>
    </StepWrapper>
  )
}

const phoneValidationSchema = Yup.object().shape({
  phone: Yup.string().phone().required()
})

const VerifyProfile = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const data = useProfile()
  const history = useHistory()

  const phoneVerified = !isNullOrEmpty(data.phone_number)

  // prevents flash of verify page before redirect, since state is only
  // undefined before loading and is null instead if unset
  const loading = typeof data.identity_verified_state === 'undefined'
  const identityVerified = data.identity_verified_state === 'verified'
  const identityVerificationPending = data.identity_verified_state === 'pending'

  const settings = useSelector(select.profile.settings)
  const phoneHidden = data.hide_phone_number === 1

  const onPhoneSubmit = async (values, { setSubmitting }) => {
    const { response } = await changePhoneNewNumber({
      phone: values.phone
    })
    if (response.ok) {
      showVerifyPhoneModal({
        initialStep: 'code',
        phone: values.phone,
        noBackButton: true,
        history
      })
    }
    setSubmitting(false)
  }

  const handleCheckboxClick = async checkboxValue => {
    const payload = { ...settings, allow_calls: checkboxValue }
    const response = await dispatch.profile.updateSettings(payload)
    if (!response?.ok) {
      toast(t('b.settings.view.settings.failed_save', 'Your settings could not be saved.'), {
        title: t('common.error', 'Error'),
        type: 'error'
      })
    }
  }

  const allowCallsDescription = !phoneHidden
    ? t(
        'r.settings.view.allow_calls.enabled.description',
        `Only landlords from the listings you've inquired about or applied to can see your phone number.`
      )
    : t(
        'r.settings.view.allow_calls.disabled.description',
        `Only landlords from the listings you've applied to can see your phone number.`
      )

  if (phoneVerified && (identityVerified || identityVerificationPending)) {
    return <Redirect to="/profile" />
  }

  if (!data || loading) return null

  return (
    <Page>
      <Helmet>
        <title>{t('b.profile.edit.verify_your_account.title', 'Verify Your Account')}</title>
      </Helmet>
      <PageTitle>{t('b.profile.edit.verify_your_account.title', 'Verify Your Account')}</PageTitle>
      <StyledContentWrapper>
        <PageHeader>
          <BackButton as={NavLink} to={'/profile'}>
            <Icon id="back_arrow" width={25} height={25} /> {t('common.back', 'Back')}
          </BackButton>
          <StepsWrapper>
            <Step
              complete={phoneVerified}
              text={t('b.profile.edit.confirm_phone.text', 'Confirm Phone')}
              showDivider
            />
            <Step
              complete={identityVerified}
              text={t('b.profile.edit.verify_id.text', 'Verify ID')}
              disabled={!phoneVerified}
            />
          </StepsWrapper>
        </PageHeader>
        <PanelContainer hSpace={30} vSpace={60}>
          <PanelInner>
            <Title>{t('b.profile.edit.setup_phone.title', 'Let’s set up your phone!')}</Title>
            <Description>
              {t(
                'b.profile.edit.setup_phone_description.text',
                'Building trust and confidence is our priority. We ask all renters to verify their phones for authentication.'
              )}
            </Description>
            <Formik
              initialValues={{ phone: data.phone_number ?? '' }}
              validationSchema={phoneValidationSchema}
              onSubmit={onPhoneSubmit}
            >
              {({ handleChange, values, isSubmitting }) => (
                <StyledForm>
                  <PhoneInput
                    name="phone"
                    placeholder={t(
                      'r.verify_phone_modal.edit.phone_number.placeholder',
                      'Phone number'
                    )}
                    value={values.phone}
                    countryCodeAlpha={data.phone_country_code_alpha}
                    onChange={handleChange('phone')}
                    disabled={isSubmitting || phoneVerified}
                  />
                  <Box marginTop="medium" style={{ maxWidth: '320px' }}>
                    <Button type="submit" disabled={isSubmitting || phoneVerified}>
                      {phoneVerified
                        ? t('b.profile.edit.phone_confirmed.button', 'Phone Confirmed')
                        : t('b.profile.edit.send_code.button', 'Send Code')}
                    </Button>
                  </Box>
                </StyledForm>
              )}
            </Formik>
          </PanelInner>
          <PanelInner>
            <CheckBoxWrapper>
              <CheckBox
                name="allow_calls"
                onChange={e => handleCheckboxClick(e.target.checked)}
                checked={!phoneHidden}
                type="checkbox"
              />
              <label htmlFor="allow_calls">
                {t('r.settings.view.allow_calls.title', 'Allow Landlords to call me')}
              </label>
            </CheckBoxWrapper>
            <DescriptionSmall>{allowCallsDescription}</DescriptionSmall>
          </PanelInner>
        </PanelContainer>
        <PanelContainer hSpace={30} vSpace={60}>
          <PanelInner disabled={!phoneVerified}>
            <VerifiedBadge type="id" verified width={32} height={32} bottom={5} />
            <Title>{t('b.profile.edit.verify_id.title', 'Verify ID')}</Title>
            <Description>
              <Trans i18nKey="b.profile.edit.verify_id_description.text">
                Become <Bold>Verified</Bold>
                <VerifiedBadge type="id" verified width={28} height={24} right={3} bottom={-5} />
                by providing a government-issued photo ID. Rest assured that your ID will never be
                shared with other people on liv.rent.
              </Trans>
            </Description>
            <Box width="full" style={{ maxWidth: '320px' }}>
              <IdVerificationButton onComplete={() => history.push(`/profile`)} />
            </Box>
          </PanelInner>
        </PanelContainer>
      </StyledContentWrapper>
    </Page>
  )
}

export default VerifyProfile
